import React, { Component } from 'react';
import { connect } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { loadOwnershipReport, downloadReport } from '../../data-dictionary-view.actions';
import ReportShell from './report-shell';

const formatOwnerEmail = (owner) => {
  if (!owner) return '—';

  // Handle raw JSON string (e.g. '[{"name":"Sriram","email":"sriram@caninsoft.com"}]')
  if (typeof owner === 'string') {
    try {
      const parsed = JSON.parse(owner);
      if (Array.isArray(parsed)) {
        const emails = parsed.map(o => o.email || o.name || '').filter(Boolean);
        return emails.length > 0 ? emails.join(', ') : '—';
      }
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed.email || parsed.name || '—';
      }
    } catch (e) {
      return owner;
    }
  }

  // Handle array of owner objects
  if (Array.isArray(owner)) {
    if (owner.length === 0) return '—';
    const emails = owner.map(o => {
      if (typeof o === 'object' && o !== null) return o.email || o.name || '';
      return o;
    }).filter(Boolean);
    return emails.length > 0 ? emails.join(', ') : '—';
  }

  // Handle single owner object
  if (typeof owner === 'object' && owner !== null) {
    return owner.email || owner.name || '—';
  }

  return owner || '—';
};

class OwnershipReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      businessOwner: '',
      techOwner: '',
      schema: '',
      orphanedOnly: false,
      search: '',
      // Owner dropdown options, cached from the UNFILTERED report load so applying an owner
      // (or any) filter — which narrows the server-returned rows — can't shrink the option
      // lists (#629). Refreshed only when all filters are cleared.
      businessOwnerOptions: [],
      techOwnerOptions: [],
    };
  }

  componentDidMount() {
    this.loadReport();
  }

  componentDidUpdate(prevProps) {
    // Rebuild the owner option lists ONLY from a successful, unfiltered, freshly-arrived load.
    // Guards: !isLoading skips the REQUEST reset; !error keeps a failed unfiltered reload from
    // blanking the cache; the identity check makes the post-setState re-render a no-op (no loop).
    // ponytail: last-write-wins — a late out-of-order unfiltered response could cache a stale
    // list; acceptable at report scale. Upgrade path: tag requests with a seq, ignore stale ones.
    const r = this.props.ownershipReport;
    if (r && !r.isLoading && !r.error && prevProps.ownershipReport !== r && this.noFiltersActive()) {
      const data = r.data || [];
      this.setState({
        businessOwnerOptions: this.getUniqueOwners(data, 'businessOwner'),
        techOwnerOptions: this.getUniqueOwners(data, 'techOwner'),
      });
    }
  }

  // Keep in sync with the filter fields loadReport sends; a filtered load must NOT refresh the cache.
  noFiltersActive() {
    const { businessOwner, techOwner, schema, orphanedOnly, search } = this.state;
    return !businessOwner && !techOwner && !schema && !orphanedOnly && !search;
  }

  loadReport = () => {
    const { businessOwner, techOwner, schema, orphanedOnly, search } = this.state;
    this.props.loadOwnershipReport({ businessOwner, techOwner, schema, orphanedOnly: orphanedOnly ? 'true' : '', search });
  }

  handleFilterChange = (field) => (e) => {
    this.setState({ [field]: e.target.value }, this.loadReport);
  }

  handleCheckboxChange = (field) => (e) => {
    this.setState({ [field]: e.target.checked }, this.loadReport);
  }

  handleDownload = () => {
    const { businessOwner, techOwner, schema, orphanedOnly, search } = this.state;
    this.props.downloadReport('ownership', { businessOwner, techOwner, schema, orphanedOnly: orphanedOnly ? 'true' : '', search });
  }

  handleCardClick = (key) => {
    if (key === 'total') {
      this.setState({ orphanedOnly: false }, this.loadReport);
    } else if (key === 'orphaned') {
      this.setState({ orphanedOnly: !this.state.orphanedOnly }, this.loadReport);
    } else {
      // Owned - clear filter
      this.setState({ orphanedOnly: false }, this.loadReport);
    }
  }

  getSummaryTiles(data) {
    const owned = data.filter(r => r.status !== 'Orphaned').length;
    const orphaned = data.filter(r => r.status === 'Orphaned').length;
    const { orphanedOnly } = this.state;
    return [
      { label: 'Total', value: data.length, className: 'total', key: 'total', isActive: !orphanedOnly, onClick: () => this.handleCardClick('total') },
      { label: 'Owned', value: owned, className: 'approved', key: 'owned', isActive: false, onClick: () => this.handleCardClick('owned') },
      { label: 'Orphaned', value: orphaned, className: 'rejected', key: 'orphaned', isActive: orphanedOnly, onClick: () => this.handleCardClick('orphaned') },
    ];
  }

  getUniqueOwners(data, field) {
    const seenEmails = new Set();
    const uniqueOwners = [];
    data.forEach(r => {
      const raw = r[field];
      if (!raw) return;

      let emails = [];
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            emails = parsed.map(owner => owner.email || owner.name || '').filter(Boolean);
          } else if (typeof parsed === 'object' && parsed !== null) {
            emails = [parsed.email || parsed.name || ''].filter(Boolean);
          } else if (raw.trim()) {
            emails = [raw.trim()];
          }
        } catch (e) {
          if (raw.trim()) emails = [raw.trim()];
        }
      } else if (Array.isArray(raw)) {
        emails = raw.map(owner => (typeof owner === 'object' && owner !== null ? owner.email || owner.name || '' : owner)).filter(Boolean);
      } else if (typeof raw === 'object' && raw !== null) {
        emails = [raw.email || raw.name || ''].filter(Boolean);
      } else if (raw) {
        emails = [String(raw)];
      }

      emails.forEach(email => {
        if (!seenEmails.has(email)) {
          seenEmails.add(email);
          uniqueOwners.push(email);
        }
      });
    });
    return uniqueOwners.sort();
  }

  renderFilters() {
    const { businessOwner, techOwner, schema, orphanedOnly, search, businessOwnerOptions, techOwnerOptions } = this.state;
    const { schemas } = this.props;
    // Options come from the cached unfiltered list (see componentDidUpdate), NOT the filtered
    // rows — otherwise selecting an owner would drop the other owners from the dropdown (#629).
    const businessOwners = businessOwnerOptions;
    const techOwners = techOwnerOptions;
    return (
      <div className="dd-report-filter-row-wrap">
        <div className="dd-view-filter-row">
          <TextField
            select
            label="Business Owner"
            value={businessOwner}
            onChange={this.handleFilterChange('businessOwner')}
            variant="standard"
            fullWidth
          >
            <MenuItem value="">All</MenuItem>
            {businessOwners.map(u => (
              <MenuItem key={u} value={u}>{formatOwnerEmail(u)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Technical Owner"
            value={techOwner}
            onChange={this.handleFilterChange('techOwner')}
            variant="standard"
            fullWidth
          >
            <MenuItem value="">All</MenuItem>
            {techOwners.map(u => (
              <MenuItem key={u} value={u}>{formatOwnerEmail(u)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Schema"
            value={schema}
            onChange={this.handleFilterChange('schema')}
            variant="standard"
            fullWidth
          >
            <MenuItem value="">All</MenuItem>
            {Array.isArray(schemas) && schemas.map(s => (
              <MenuItem key={s.schemaId} value={s.schemaId}>{s.instanceName} ({s.schemaName})</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Search"
            value={search}
            onChange={this.handleFilterChange('search')}
            variant="standard"
            fullWidth
            InputProps={{
              endAdornment: search && (
                <InputAdornment position="end">
                  <i className="material-icons search-clear" onClick={() => this.setState({ search: '' }, this.loadReport)}>close</i>
                </InputAdornment>
              )
            }}
          />
        </div>
        <div className="dd-report-checkbox-row">
          <FormControlLabel
            control={<Checkbox checked={orphanedOnly} onChange={this.handleCheckboxChange('orphanedOnly')} color="primary" />}
            label="Show only orphaned assets"
          />
        </div>
      </div>
    );
  }

  renderTable(data) {
    return (
      <div className="object-table-container">
        <table className="object-table">
          <thead>
            <tr>
              <th>Schema</th>
              <th>Table</th>
              <th>Business Owner</th>
              <th>Tech Owner</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                <td>{row.schema}</td>
                <td>{row.table}</td>
                <td>{formatOwnerEmail(row.businessOwner)}</td>
                <td>{formatOwnerEmail(row.techOwner)}</td>
                <td>
                  <span className={`dd-badge ${row.status === 'Orphaned' ? 'badge-orphaned' : 'badge-owned'}`}>
                    {row.status || 'Owned'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { ownershipReport, onBack } = this.props;
    const data = ownershipReport.data || [];

    return (
      <ReportShell
        title="Ownership & Stewardship Report"
        icon="people"
        summaryTiles={this.getSummaryTiles(data)}
        filters={this.renderFilters()}
        table={this.renderTable(data)}
        isLoading={ownershipReport.isLoading}
        error={ownershipReport.error}
        data={data}
        onBack={onBack}
        onDownload={this.handleDownload}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  ownershipReport: state.dataDictionaryViewReducer.reports.ownership,
  schemas: state.dataDictionaryViewReducer.schemas,
});

export default connect(mapStateToProps, { loadOwnershipReport, downloadReport })(OwnershipReport);
